import { useState } from "react";
import { Clapperboard, Theater, MonitorPlay, BookOpen, CalendarHeart, Sparkles, Send } from "lucide-react";
import { useLang } from "../i18n";
import api, { formatApiError } from "../api";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";

const TOPICS = [
  { key: "teatro", icon: Theater },
  { key: "cinema", icon: Clapperboard },
  { key: "streaming", icon: MonitorPlay },
  { key: "editoria", icon: BookOpen },
  { key: "eventi", icon: CalendarHeart },
  { key: "altro", icon: Sparkles },
];

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", topic: "cinema", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [hp, setHp] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/contact", { ...form, website: hp });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-lg border border-white/15 bg-black/40 px-4 py-3.5 text-sm placeholder:text-mute focus:border-ember focus:outline-none transition-colors";

  return (
    <main className="pt-16" data-testid="contact-page">
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-20 pb-24">
        <Reveal>
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-5">{t("contact_kicker")}</p>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight leading-tight mb-5" data-testid="contact-title">
            {t("contact_title")}
          </h1>
          <p className="text-mist max-w-xl text-sm sm:text-base mb-12">{t("contact_sub")}</p>
        </Reveal>

        {sent ? (
          <Reveal>
            <div className="rounded-2xl border border-gold/40 bg-gold/5 p-10 text-center" data-testid="contact-success">
              <p className="font-display font-bold text-2xl text-gold mb-3">{t("contact_sent_title")}</p>
              <p className="text-mist">{t("contact_sent_sub")}</p>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-card p-6 sm:p-10 space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="contact-topics">
                {TOPICS.map(({ key, icon: Icon }) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`contact-topic-${key}`}
                    onClick={() => setForm({ ...form, topic: key })}
                    className={`flex flex-col items-center gap-2 rounded-xl border p-4 transition-all duration-300 ${
                      form.topic === key
                        ? "border-ember bg-ember/10 text-ember"
                        : "border-white/10 text-mist hover:border-white/30"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-mono2 text-[10px] tracking-[0.15em] uppercase text-center">{t(`topic_${key}`)}</span>
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name-input" required className={field} placeholder={t("contact_name")}
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input data-testid="contact-email-input" required type="email" className={field} placeholder={t("contact_email")}
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <textarea data-testid="contact-message-input" required minLength={10} rows={6} className={`${field} resize-none`} placeholder={t("contact_message")}
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              {error && <p data-testid="contact-error" className="text-sm text-red-400">{error}</p>}
              <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />
              <button data-testid="contact-submit-button" disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-ember px-8 py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors disabled:opacity-50">
                {t("contact_send")} <Send className="w-4 h-4" />
              </button>
            </form>
          </Reveal>
        )}
      </section>
      <Marquee text={t("marquee")} />
    </main>
  );
};

export default Contact;
